import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { Gift, TrendingUp, Users, Ticket, Coins, GraduationCap, Tag } from "lucide-react";

export default function AdminPromotions() {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);

  useEffect(() => {
    adminClient().get("/admin/promotions/spins").then(({ data }) => setData(data)).catch(() => setData({ recent: [] }));
    adminClient().get("/admin/subscribers").then(({ data }) => setSubs(data)).catch(() => setSubs({ subscribers: [], segments: {} }));
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const stats = [
    { label: "Total Spins", value: data.total || 0, icon: TrendingUp },
    { label: "Discount Wins", value: data.discount_wins || 0, icon: Tag },
    { label: "Coin Wins", value: data.coin_wins || 0, icon: Coins },
    { label: "Coins Awarded", value: data.coins_awarded || 0, icon: Coins },
    { label: "Free Class Wins", value: data.class_wins || 0, icon: GraduationCap },
    { label: "Leads Captured", value: data.leads_captured || 0, icon: Users },
  ];

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Spin &amp; Win (v2)</h1></div>
      <p className="text-sm text-muted-foreground mb-6">Multi-reward front-page wheel — product discounts, TAVEN Coins &amp; Free Class vouchers. Status: <strong>{data.enabled ? "Live" : "Disabled"}</strong></p>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 mb-6">
        <div className="flex items-center gap-2 font-bold mb-1"><Ticket className="w-4 h-4" /> Manual setup required (storefront discount codes)</div>
        Create these codes in your storefront — each set to <strong>Products only</strong> (exclude subscriptions/certificates): {" "}
        {(data.codes || []).map((c, i) => (
          <span key={c}><strong className="text-amber-900">{c}</strong> ({[1, 3, 5, 7, 10][i]}% off){i < (data.codes.length - 1) ? ", " : "."}</span>
        ))}
        {" "}Coin &amp; Free-Class rewards are credited automatically to signed-in accounts (no storefront setup needed). Percentage discounts never reduce course, certificate, affiliate or seller prices.
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="border border-black/10 bg-white px-4 py-3 min-w-[150px]">
            <div className="flex items-center gap-2 text-muted-foreground"><s.icon className="w-4 h-4 text-taven-gold" /><p className="text-xs uppercase tracking-wider">{s.label}</p></div>
            <p className="text-2xl font-black font-display mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr>
            <th className="px-4 py-3 font-bold">Reward</th><th className="px-4 py-3 font-bold">Type</th>
            <th className="px-4 py-3 font-bold">Code / Coins</th><th className="px-4 py-3 font-bold">Email</th>
            <th className="px-4 py-3 font-bold">Claim</th>
            <th className="px-4 py-3 font-bold">Streak</th><th className="px-4 py-3 font-bold">When</th>
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {(data.recent || []).map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-3"><span className="text-green-700 font-bold">{r.result}</span></td>
                <td className="px-4 py-3 text-muted-foreground text-xs uppercase">{r.reward_type || "—"}</td>
                <td className="px-4 py-3">{r.code || (r.coins ? `${r.coins} coins` : "—")}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.email || "guest"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase ${r.claim_status === "claimed" ? "text-green-700" : "text-muted-foreground"}`}>
                    {r.claim_status || "unclaimed"}{r.redemption_status && r.claim_status === "claimed" ? ` · ${r.redemption_status}` : ""}
                  </span>
                </td>
                <td className="px-4 py-3">{r.streak || 1}</td>
                <td className="px-4 py-3 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {(!data.recent || data.recent.length === 0) && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No spins yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Subscribers (email + SMS consent) */}
      <div className="mt-10" data-testid="admin-subscribers">
        <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-taven-gold" /><h2 className="text-xl font-black font-display">Subscribers &amp; SMS Consent</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Email + optional SMS opt-ins with consent tracking &amp; segmentation.</p>
        <div className="flex flex-wrap gap-3 mb-4">
          {[
            { label: "Total", value: subs?.total || 0 },
            { label: "Email Only", value: subs?.segments?.email_only || 0 },
            { label: "SMS Only", value: subs?.segments?.sms_only || 0 },
            { label: "Email + SMS", value: subs?.segments?.both || 0 },
            { label: "SMS Consented", value: subs?.segments?.sms_consented || 0 },
          ].map((s) => (
            <div key={s.label} className="border border-black/10 bg-white px-4 py-3 min-w-[130px]">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-black font-display mt-1">{s.value}</p>
            </div>
          ))}
        </div>
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-taven-gray/60 text-left"><tr>
              <th className="px-4 py-3 font-bold">Email</th><th className="px-4 py-3 font-bold">Phone</th>
              <th className="px-4 py-3 font-bold">Lang</th><th className="px-4 py-3 font-bold">Channel</th>
              <th className="px-4 py-3 font-bold">SMS Consent</th><th className="px-4 py-3 font-bold">Source</th>
            </tr></thead>
            <tbody className="divide-y divide-black/5">
              {(subs?.subscribers || []).slice(0, 100).map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.phone || "—"}</td>
                  <td className="px-4 py-3 uppercase text-xs">{s.language}</td>
                  <td className="px-4 py-3 text-xs uppercase">{s.channel}</td>
                  <td className="px-4 py-3 text-xs">{s.sms_consent ? <span className="text-green-700 font-bold">Yes</span> : "No"}{s.consent_date ? ` · ${new Date(s.consent_date).toLocaleDateString()}` : ""}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{s.campaign_source || s.source_page || "—"}</td>
                </tr>
              ))}
              {(!subs?.subscribers || subs.subscribers.length === 0) && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No subscribers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
